// src/components/admin/employee/payroll/AdminPayrollViewer.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../../../../firebaseConfig";
import { isRejectedStatus } from "../../../../utils/userStatus";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  orderBy,
  Timestamp,
  limit,
  startAfter,
} from "firebase/firestore";

import "./AdminPayrollViewer.css";
import PayrollPager from "./PayrollPager";

const parseInputDateTime = (dateStr, timeStr) => {
  const combined = new Date(`${dateStr} ${timeStr}`);
  return Timestamp.fromDate(combined);
};

const BROWSE_PAGE_SIZE = 25;
const GENERATED_PAGE_SIZE = 70;

const buildPayrollReportKey = (tutorId, startDate, endDate) =>
  tutorId && startDate && endDate ? `${tutorId}::${startDate}::${endDate}` : "";

const buildAppliedRouteState = ({ filterRole, filterStatus, selectedTutor, startDate, endDate }) => ({
  filterRole,
  filterStatus,
  selectedTutor,
  startDate,
  endDate,
});

const sameAppliedRouteState = (left, right) =>
  !!left &&
  !!right &&
  left.filterRole === right.filterRole &&
  left.filterStatus === right.filterStatus &&
  left.selectedTutor === right.selectedTutor &&
  left.startDate === right.startDate &&
  left.endDate === right.endDate;

const getDayStamp = (ts) => {
  if (!ts) return 0;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return day.getTime();
};

const sortRowsDateThenTutor = (rows, dir) => {
  const direction = dir === "asc" ? 1 : -1;
  const cloned = [...rows];

  cloned.sort((a, b) => {
    const ad = getDayStamp(a.startTime);
    const bd = getDayStamp(b.startTime);
    if (ad !== bd) return (ad - bd) * direction;

    const an = String(a.tutorName || "").toLowerCase();
    const bn = String(b.tutorName || "").toLowerCase();
    if (an !== bn) return an.localeCompare(bn);

    const ast = a.startTime?.toMillis ? a.startTime.toMillis() : 0;
    const bst = b.startTime?.toMillis ? b.startTime.toMillis() : 0;
    return ast - bst;
  });

  return cloned;
};

const AdminPayrollViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterRole,   setFilterRole]   = useState("tutor");
  const [filterStatus, setFilterStatus] = useState("active");
  const [selectedTutor, setSelectedTutor] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [records, setRecords] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const [summary, setSummary] = useState({
    adminHours: 0,
    adminPay: 0,
    teachingHours: 0,
    teachingPay: 0,
  });

  const [currentTutorRates, setCurrentTutorRates] = useState({ admin: 0, teaching: 0 });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [listSortDir, setListSortDir] = useState("desc");
  const [listPage, setListPage] = useState(1);

  const [browseLoading, setBrowseLoading] = useState(false);
  const [browsePages, setBrowsePages] = useState([]);
  const [browsePageIndex, setBrowsePageIndex] = useState(0);
  const routeApplyInFlightRef = useRef(false);
  const currentGeneratedKeyRef = useRef("");
  const appliedReportStateRef = useRef(null);
  const currentStateRef = useRef({
    filterRole: "tutor",
    filterStatus: "active",
    selectedTutor: "",
    startDate: "",
    endDate: "",
    hasGenerated: false,
    listSortDir: "desc",
    listPage: 1,
  });

  const routeState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const role = params.get("role");
    const status = params.get("status");
    const sort = params.get("sort");
    const parsedPage = Number(params.get("page") || "1");

    return {
      filterRole: ["all", "tutor", "admin"].includes(role) ? role : "tutor",
      filterStatus: ["all", "active", "on hold", "finished"].includes(status) ? status : "active",
      selectedTutor: params.get("employee") || "",
      startDate: params.get("start") || "",
      endDate: params.get("end") || "",
      hasGenerated: params.get("generated") === "1",
      listSortDir: sort === "asc" ? "asc" : "desc",
      listPage: Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1,
    };
  }, [location.search]);

  // --- Load tutors ---
  useEffect(() => {
    const fetchTutors = async () => {
      const q = query(collection(db, "users"), where("role", "in", ["tutor", "admin"]));
      const snap = await getDocs(q);
      const list = snap.docs
        .map((d) => ({
          id: d.id,
          name: d.data().name || "Unknown Name",
          role: (d.data().role || "tutor").toLowerCase(),
          adminRate: d.data().adminRate || 0,
          teachingRate: d.data().teachingRate || 0,
          status: (d.data().status || "active").toLowerCase(),
        }))
        .filter((user) => !isRejectedStatus(user.status));

      list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      setTutors(list);
    };
    fetchTutors();
  }, []);

  // Derived list filtered by Role + Status dropdowns
  const filteredTutors = useMemo(() => {
    return tutors.filter((t) => {
      const roleOk   = filterRole   === "all" || t.role   === filterRole;
      const statusOk = filterStatus === "all" || t.status === filterStatus;
      return roleOk && statusOk;
    });
  }, [tutors, filterRole, filterStatus]);

  // Clear selected employee if it disappears from the filtered list
  useEffect(() => {
    currentStateRef.current = {
      filterRole,
      filterStatus,
      selectedTutor,
      startDate,
      endDate,
      hasGenerated,
      listSortDir,
      listPage,
    };
  }, [endDate, filterRole, filterStatus, hasGenerated, listPage, listSortDir, selectedTutor, startDate]);

  useEffect(() => {
    if (selectedTutor && !filteredTutors.find((t) => t.id === selectedTutor)) {
      setSelectedTutor("");
    }
  }, [filteredTutors, selectedTutor]);

  useEffect(() => {
    if (selectedTutor) {
      const t = tutors.find((u) => u.id === selectedTutor);
      if (t) setCurrentTutorRates({ admin: t.adminRate, teaching: t.teachingRate });
    } else {
      setCurrentTutorRates({ admin: 0, teaching: 0 });
    }
  }, [selectedTutor, tutors]);

  // --- Browse (recent) pagination ---
  const resetBrowse = async () => {
    setBrowsePages([]);
    setBrowsePageIndex(0);
    setListPage(1);
    await fetchBrowsePage(0, true);
  };

  const fetchBrowsePage = async (targetIndex, force = false) => {
    if (browseLoading) return;
    if (!force && browsePages[targetIndex]) {
      setBrowsePageIndex(targetIndex);
      return;
    }

    setBrowseLoading(true);
    try {
      const dir = listSortDir === "asc" ? "asc" : "desc";
      let qRef = query(
        collection(db, "payroll_records"),
        orderBy("startTime", dir),
        limit(BROWSE_PAGE_SIZE)
      );

      if (targetIndex > 0) {
        const prev = browsePages[targetIndex - 1];
        if (prev?.lastDoc) {
          qRef = query(
            collection(db, "payroll_records"),
            orderBy("startTime", dir),
            startAfter(prev.lastDoc),
            limit(BROWSE_PAGE_SIZE)
          );
        }
      }

      const snap = await getDocs(qRef);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      const hasNext = snap.docs.length === BROWSE_PAGE_SIZE;
      const sortedRows = sortRowsDateThenTutor(rows, listSortDir);

      setBrowsePages((prev) => {
        const next = [...prev];
        next[targetIndex] = { rows: sortedRows, lastDoc, hasNext };
        return next;
      });
      setBrowsePageIndex(targetIndex);
    } catch (e) {
      console.error(e);
      alert("Failed to load recent payroll records. See console.");
    } finally {
      setBrowseLoading(false);
    }
  };

  useEffect(() => {
    if (!hasGenerated) resetBrowse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasGenerated) resetBrowse();
    else setListPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSortDir]);

  const calculateSummary = useCallback((data) => {
    let adminH = 0, adminP = 0, teachH = 0, teachP = 0;
    data.forEach((r) => {
      if (r.type === "admin") {
        adminH += r.hours;
        adminP += r.totalPay;
      } else {
        teachH += r.hours;
        teachP += r.totalPay;
      }
    });
    setSummary({
      adminHours: parseFloat(adminH.toFixed(2)),
      adminPay: parseFloat(adminP.toFixed(2)),
      teachingHours: parseFloat(teachH.toFixed(2)),
      teachingPay: parseFloat(teachP.toFixed(2)),
    });
  }, []);

  const runGenerate = useCallback(async ({ tutorId, start, end, role, status }) => {
    if (!tutorId || !start || !end) return false;

    setLoading(true);
    setHasGenerated(true);

    try {
      const startTs = Timestamp.fromDate(new Date(`${start} 00:00:00`));
      const endTs = Timestamp.fromDate(new Date(`${end} 23:59:59`));

      const qRef = query(
        collection(db, "payroll_records"),
        where("uid", "==", tutorId),
        where("startTime", ">=", startTs),
        where("startTime", "<=", endTs),
        orderBy("startTime", "asc")
      );

      const snap = await getDocs(qRef);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRecords(data);
      calculateSummary(data);
      currentGeneratedKeyRef.current = buildPayrollReportKey(tutorId, start, end);
      appliedReportStateRef.current = buildAppliedRouteState({
        filterRole: role || filterRole,
        filterStatus: status || filterStatus,
        selectedTutor: tutorId,
        startDate: start,
        endDate: end,
      });
      return true;
    } catch (error) {
      console.error("Error fetching records:", error);
      alert("Error loading data. See console.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [calculateSummary, filterRole, filterStatus]);

  const navigateToAppliedReport = useCallback((appliedState, sortDir, page) => {
    if (!appliedState) return;

    const params = new URLSearchParams();
    params.set("role", appliedState.filterRole);
    params.set("status", appliedState.filterStatus);
    params.set("sort", sortDir);
    params.set("employee", appliedState.selectedTutor);
    params.set("start", appliedState.startDate);
    params.set("end", appliedState.endDate);
    params.set("generated", "1");
    params.set("page", String(page));

    const nextUrl = `${location.pathname}?${params.toString()}`;
    const currentUrl = `${location.pathname}${location.search}`;
    if (currentUrl !== nextUrl) {
      navigate(nextUrl, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // --- Generate report ---
  const handleGenerate = async () => {
    if (!selectedTutor || !startDate || !endDate) {
      alert("Please select an employee and a date range.");
      return;
    }

    setListSortDir("asc");
    setListPage(1);
    const generated = await runGenerate({
      tutorId: selectedTutor,
      start: startDate,
      end: endDate,
      role: filterRole,
      status: filterStatus,
    });
    if (generated) {
      navigateToAppliedReport(
        buildAppliedRouteState({
          filterRole,
          filterStatus,
          selectedTutor,
          startDate,
          endDate,
        }),
        "asc",
        1
      );
    }
  };

  const handleClear = () => {
    setRecords([]);
    setHasGenerated(false);
    setSummary({ adminHours: 0, adminPay: 0, teachingHours: 0, teachingPay: 0 });
    setSelectedTutor("");
    setStartDate("");
    setEndDate("");
    setFilterRole("tutor");
    setFilterStatus("active");
    setListSortDir("desc");
    setEditingId(null);
    setEditForm({});
    setListPage(1);
    currentGeneratedKeyRef.current = "";
    appliedReportStateRef.current = null;
    if (location.search) {
      navigate("/employee/payroll", { replace: true });
    }
  };

  useEffect(() => {
    const snapshot = currentStateRef.current;

    routeApplyInFlightRef.current = true;

    if (snapshot.filterRole !== routeState.filterRole) setFilterRole(routeState.filterRole);
    if (snapshot.filterStatus !== routeState.filterStatus) setFilterStatus(routeState.filterStatus);
    if (snapshot.selectedTutor !== routeState.selectedTutor) setSelectedTutor(routeState.selectedTutor);
    if (snapshot.startDate !== routeState.startDate) setStartDate(routeState.startDate);
    if (snapshot.endDate !== routeState.endDate) setEndDate(routeState.endDate);
    if (snapshot.listSortDir !== routeState.listSortDir) setListSortDir(routeState.listSortDir);
    if (snapshot.listPage !== routeState.listPage) setListPage(routeState.listPage);
    if (!routeState.hasGenerated && snapshot.hasGenerated) {
      setHasGenerated(false);
      setRecords([]);
      setSummary({ adminHours: 0, adminPay: 0, teachingHours: 0, teachingPay: 0 });
      currentGeneratedKeyRef.current = "";
      appliedReportStateRef.current = null;
    }

    requestAnimationFrame(() => {
      routeApplyInFlightRef.current = false;
    });
  }, [routeState]);

  useEffect(() => {
    if (!routeState.hasGenerated) return;
    if (!routeState.selectedTutor || !routeState.startDate || !routeState.endDate) return;

    const routeKey = buildPayrollReportKey(routeState.selectedTutor, routeState.startDate, routeState.endDate);
    const nextAppliedRouteState = buildAppliedRouteState({
      filterRole: routeState.filterRole,
      filterStatus: routeState.filterStatus,
      selectedTutor: routeState.selectedTutor,
      startDate: routeState.startDate,
      endDate: routeState.endDate,
    });

    if (hasGenerated && currentGeneratedKeyRef.current === routeKey) {
      if (!sameAppliedRouteState(appliedReportStateRef.current, nextAppliedRouteState)) {
        appliedReportStateRef.current = nextAppliedRouteState;
      }
      return;
    }

    let cancelled = false;

    (async () => {
      const ok = await runGenerate({
        tutorId: routeState.selectedTutor,
        start: routeState.startDate,
        end: routeState.endDate,
        role: routeState.filterRole,
        status: routeState.filterStatus,
      });
      if (!ok || cancelled) return;
      if (routeState.listSortDir !== currentStateRef.current.listSortDir) {
        setListSortDir(routeState.listSortDir);
      }
      if (routeState.listPage !== currentStateRef.current.listPage) {
        setListPage(routeState.listPage);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    hasGenerated,
    routeState.endDate,
    routeState.filterRole,
    routeState.filterStatus,
    routeState.hasGenerated,
    routeState.listPage,
    routeState.listSortDir,
    routeState.selectedTutor,
    routeState.startDate,
    runGenerate,
  ]);

  useEffect(() => {
    if (routeApplyInFlightRef.current) return;
    if (!hasGenerated || !appliedReportStateRef.current) return;
    navigateToAppliedReport(appliedReportStateRef.current, listSortDir, listPage);
  }, [hasGenerated, listPage, listSortDir, navigateToAppliedReport]);

  // --- Single record edit ---
  const startEdit = (record) => {
    setEditingId(record.id);
    const st = record.startTime.toDate().toTimeString().slice(0, 5);
    const et = record.endTime.toDate().toTimeString().slice(0, 5);
    setEditForm({ startTime: st, endTime: et, rateUsed: record.rateUsed, type: record.type });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (record) => {
    try {
      const dateStr = record.date;
      const newStartTs = parseInputDateTime(dateStr, editForm.startTime);
      const newEndTs = parseInputDateTime(dateStr, editForm.endTime);

      const newRate = parseFloat(editForm.rateUsed);
      if (isNaN(newRate) || newRate < 0) {
        alert("Please enter a valid rate.");
        return;
      }

      const durationMs = newEndTs.toDate() - newStartTs.toDate();
      const rawHours = durationMs / (1000 * 60 * 60);
      if (rawHours <= 0) {
        alert("End time must be after start time.");
        return;
      }

      const roundedHours = Math.round(rawHours * 100) / 100;
      const newPay = parseFloat((roundedHours * newRate).toFixed(2));

      const updates = {
        startTime: newStartTs,
        endTime: newEndTs,
        rateUsed: newRate,
        hours: roundedHours,
        totalPay: newPay,
        type: editForm.type,
      };

      await updateDoc(doc(db, "payroll_records", record.id), updates);

      if (hasGenerated) {
        const updatedRecords = records.map((r) => (r.id === record.id ? { ...r, ...updates } : r));
        setRecords(updatedRecords);
        calculateSummary(updatedRecords);
      } else {
        setBrowsePages((prev) =>
          prev.map((p) =>
            !p ? p : { ...p, rows: p.rows.map((r) => (r.id === record.id ? { ...r, ...updates } : r)) }
          )
        );
      }

      setEditingId(null);
    } catch (error) {
      console.error("Error updating:", error);
      alert("Failed to update record.");
    }
  };

  // --- Delete record ---
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      await deleteDoc(doc(db, "payroll_records", id));

      if (hasGenerated) {
        const filtered = records.filter((r) => r.id !== id);
        setRecords(filtered);
        calculateSummary(filtered);
        const pageCount = Math.max(1, Math.ceil(filtered.length / GENERATED_PAGE_SIZE));
        if (listPage > pageCount) setListPage(pageCount);
      } else {
        setBrowsePages((prev) =>
          prev.map((p) => (!p ? p : { ...p, rows: p.rows.filter((r) => r.id !== id) }))
        );
      }
    } catch (error) {
      console.error("Error deleting:", error);
      alert("Failed to delete record.");
    }
  };

  // --- Derived display values ---
  const isBrowseMode = !hasGenerated;

  const allGeneratedSorted = useMemo(() => {
    if (!hasGenerated) return [];
    return sortRowsDateThenTutor(records, listSortDir);
  }, [records, listSortDir, hasGenerated]);

  const generatedPageCount = useMemo(
    () => Math.max(1, Math.ceil(allGeneratedSorted.length / GENERATED_PAGE_SIZE)),
    [allGeneratedSorted.length]
  );

  const visibleGeneratedRows = useMemo(() => {
    if (!hasGenerated) return [];
    const start = (listPage - 1) * GENERATED_PAGE_SIZE;
    return allGeneratedSorted.slice(start, start + GENERATED_PAGE_SIZE);
  }, [allGeneratedSorted, listPage, hasGenerated]);

  const browseCurrent = browsePages[browsePageIndex];
  const visibleBrowseRows = browseCurrent?.rows || [];
  const browseCanPrev = browsePageIndex > 0;
  const browseCanNext = !!browseCurrent?.hasNext;

  const visibleRows = isBrowseMode ? visibleBrowseRows : visibleGeneratedRows;
  const listTitle = isBrowseMode ? "Recent Payroll Records" : "Generated Payroll Records";

  const displayPageLabel = isBrowseMode ? browsePageIndex + 1 : listPage;

  const handlePrevPage = () => {
    if (isBrowseMode) fetchBrowsePage(browsePageIndex - 1);
    else setListPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    if (isBrowseMode) fetchBrowsePage(browsePageIndex + 1);
    else setListPage((p) => Math.min(generatedPageCount, p + 1));
  };

  const canPrev = isBrowseMode ? browseCanPrev : listPage > 1;
  const canNext = isBrowseMode ? browseCanNext : listPage < generatedPageCount;

  const totalPay = summary.adminPay + summary.teachingPay;
  const totalHours = parseFloat((summary.adminHours + summary.teachingHours).toFixed(2));

  return (
    <div className="apv-page">

      {/* ── Page header ── */}
      <div className="apv-pageHeader">
        <div className="apv-pageTitle">Payroll</div>
      </div>

      {/* ── Controls card ── */}
      <div className="apv-controlCard">
        <div className="apv-controlRow">
          <div className="apv-controlField apv-controlFieldNarrow">
            <label className="apv-fieldLabel">Role</label>
            <select
              className="apv-select"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="tutor">Tutor</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="apv-controlField apv-controlFieldNarrow">
            <label className="apv-fieldLabel">Status</label>
            <select
              className="apv-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="on hold">On Hold</option>
              <option value="finished">Finished</option>
            </select>
          </div>

          <div className="apv-controlField">
            <label className="apv-fieldLabel">Employee</label>
            <select
              className="apv-select"
              value={selectedTutor}
              onChange={(e) => setSelectedTutor(e.target.value)}
            >
              <option value="">— Choose employee —</option>
              {filteredTutors.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="apv-controlField apv-controlFieldNarrow">
            <label className="apv-fieldLabel">From</label>
            <input
              type="date"
              className="apv-dateInput"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="apv-controlField apv-controlFieldNarrow">
            <label className="apv-fieldLabel">Up To</label>
            <input
              type="date"
              className="apv-dateInput"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="apv-controlActions">
            <button className="apv-btnPrimary" onClick={handleGenerate} disabled={loading}>
              {loading ? "Loading…" : "Generate"}
            </button>
            <button className="apv-btnGhost" onClick={handleClear}>
              Clear
            </button>
          </div>
        </div>

      </div>

      {/* ── Pay Summary (only after Generate) ── */}
      {hasGenerated && <div className="apv-summaryCard">
        {selectedTutor && (
          <div className="apv-summaryRates">
            <span>Admin rate: <strong>${currentTutorRates.admin}/hr</strong></span>
            <span>Teaching rate: <strong>${currentTutorRates.teaching}/hr</strong></span>
          </div>
        )}

        <div className="apv-summaryTiles">
          <div className="apv-summaryTile">
            <div className="apv-summaryTileLabel">Admin</div>
            <div className="apv-summaryTileHours">{summary.adminHours}h</div>
            <div className="apv-summaryTileValue">${summary.adminPay.toFixed(2)}</div>
          </div>

          <div className="apv-summaryDivider" />

          <div className="apv-summaryTile">
            <div className="apv-summaryTileLabel">Teaching</div>
            <div className="apv-summaryTileHours">{summary.teachingHours}h</div>
            <div className="apv-summaryTileValue">${summary.teachingPay.toFixed(2)}</div>
          </div>

          <div className="apv-summaryDivider" />

          <div className="apv-summaryTile">
            <div className="apv-summaryTileLabel">Total Pay</div>
            <div className="apv-summaryTileHours">{totalHours}h</div>
            <div className="apv-summaryTileValueTotal">${totalPay.toFixed(2)}</div>
          </div>
        </div>
      </div>}

      {/* ── Records section (table + pager, fills remaining height) ── */}
      <div className="apv-tableSection">
      <div className="apv-tableTopBar">
        <div>
          <div className="apv-tableTitle">{listTitle}</div>
        </div>

        <div className="apv-tableTopRight">
          <div className="apv-pageMeta">
            Page <strong>{displayPageLabel}</strong> &middot;{" "}
            <strong>{isBrowseMode ? BROWSE_PAGE_SIZE : GENERATED_PAGE_SIZE}</strong> per page
            {!isBrowseMode && (
              <> &middot; <strong>{allGeneratedSorted.length}</strong> total</>
            )}
          </div>
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="apv-tableCard">
        <div className="apv-tableScroll">
          <table className="apv-table">
            <thead>
              <tr>
                <th
                  className="apv-thSortable"
                  onClick={() => setListSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                >
                  <span className="apv-thSortInner">
                    Date
                    <span className="apv-sortArrow">
                      {listSortDir === "desc" ? "↓" : "↑"}
                    </span>
                  </span>
                </th>
                <th>Name</th>
                <th>Type</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Hrs</th>
                <th>Rate</th>
                <th>Total</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan="9" className="apv-emptyCell">
                    {loading || browseLoading
                      ? "Loading records…"
                      : hasGenerated
                      ? "No records found for this employee and date range."
                      : "No payroll records found."}
                  </td>
                </tr>
              ) : (
                (() => {
                  let groupCounter = 0;
                  return visibleRows.map((row, index) => {
                    if (index > 0 && row.date !== visibleRows[index - 1].date) {
                      groupCounter++;
                    }
                    const rowClass = groupCounter % 2 !== 0 ? "apv-row-alt-group" : "";

                    return (
                      <tr key={row.id} className={rowClass}>
                        <td>{row.date}</td>
                        <td>{row.tutorName}</td>

                        {editingId === row.id ? (
                          <>
                            <td>
                              <select
                                className="apv-editSelect"
                                value={editForm.type}
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  const tutor = tutors.find((t) => t.id === row.uid);
                                  const rates = tutor
                                    ? { admin: tutor.adminRate, teaching: tutor.teachingRate }
                                    : currentTutorRates;
                                  const newRate = newType === "admin" ? rates.admin : rates.teaching;
                                  setEditForm({ ...editForm, type: newType, rateUsed: newRate });
                                }}
                              >
                                <option value="admin">Admin</option>
                                <option value="teaching">Teaching</option>
                              </select>
                            </td>
                            <td>
                              <input
                                type="time"
                                className="apv-timeInput"
                                value={editForm.startTime}
                                onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                              />
                            </td>
                            <td>
                              <input
                                type="time"
                                className="apv-timeInput"
                                value={editForm.endTime}
                                onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                              />
                            </td>
                            <td className="apv-muted">—</td>
                            <td>
                              <input
                                type="number"
                                step="0.01"
                                className="apv-rateInput"
                                value={editForm.rateUsed}
                                onChange={(e) => setEditForm({ ...editForm, rateUsed: e.target.value })}
                              />
                            </td>
                            <td className="apv-muted">—</td>
                            <td>
                              <div className="apv-rowActions">
                                <button className="apv-link apv-linkGood" onClick={() => saveEdit(row)}>
                                  Save
                                </button>
                                <button className="apv-link apv-linkMuted" onClick={cancelEdit}>
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              <span className={`apv-pill ${row.type === "admin" ? "apv-pillAdmin" : "apv-pillTeach"}`}>
                                {row.type === "admin" ? "Admin" : "Teaching"}
                              </span>
                            </td>
                            <td>
                              {row.startTime
                                ? row.startTime.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                : "—"}
                            </td>
                            <td>
                              {row.endTime
                                ? row.endTime.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                : "—"}
                            </td>
                            <td>{row.hours}</td>
                            <td>${row.rateUsed}</td>
                            <td className="apv-strong">
                              ${row.totalPay?.toFixed ? row.totalPay.toFixed(2) : Number(row.totalPay || 0).toFixed(2)}
                            </td>
                            <td>
                              <div className="apv-rowActions">
                                <button className="apv-link apv-linkEdit" onClick={() => startEdit(row)}>
                                  Edit
                                </button>
                                <button className="apv-link apv-linkBad" onClick={() => handleDelete(row.id)}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <div className="apv-pagerWrap">
          <PayrollPager
            canPrev={canPrev}
            canNext={canNext}
            onPrev={handlePrevPage}
            onNext={handleNextPage}
          />
        </div>
      </div>
      </div>{/* end apv-tableSection */}

    </div>
  );
};

export default AdminPayrollViewer;
